import { View, Pressable, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { ThemedText, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { Document, DocumentMetadata } from '@/services/documentVault';
import { getDocumentExpirationDate, getExpirationStatus } from '@/utils/documentExpiration';
import { useState, useEffect } from 'react';
import { isDocumentOffline } from '@/services/offlineDocuments';

type DocumentCardProps = {
  document: Document;
  onPress: () => void;
  onDelete?: () => void;
};

const DOCUMENT_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  passport: 'book-outline',
  visa: 'document-text-outline',
  driving_license: 'car-outline',
  health_record: 'medical-outline',
  insurance: 'shield-checkmark-outline',
  other: 'document-outline',
};

// Type-specific color palette for wallet-style cards
const DOCUMENT_TYPE_COLORS: Record<string, { primary: string; secondary: string; gradient: string[] }> = {
  passport: {
    primary: '#1E3A8A',
    secondary: '#3B82F6',
    gradient: ['#1E3A8A', '#2563EB', '#3B82F6'],
  },
  visa: {
    primary: '#7C3AED',
    secondary: '#A78BFA',
    gradient: ['#7C3AED', '#8B5CF6', '#A78BFA'],
  },
  driving_license: {
    primary: '#F97316',
    secondary: '#FB923C',
    gradient: ['#F97316', '#FB923C', '#FDBA74'],
  },
  health_record: {
    primary: '#DC2626',
    secondary: '#EF4444',
    gradient: ['#DC2626', '#EF4444', '#F87171'],
  },
  insurance: {
    primary: '#059669',
    secondary: '#10B981',
    gradient: ['#059669', '#10B981', '#34D399'],
  },
  other: {
    primary: '#6B7280',
    secondary: '#9CA3AF',
    gradient: ['#6B7280', '#9CA3AF', '#D1D5DB'],
  },
};

const getMetadataIdentifier = (metadata?: DocumentMetadata) => {
  if (!metadata) return null;
  if ('number' in metadata && typeof metadata.number === 'string') {
    return `#${metadata.number}`;
  }
  if ('policyNumber' in metadata && typeof metadata.policyNumber === 'string') {
    return `Policy: ${metadata.policyNumber}`;
  }
  if ('documentNumber' in metadata && typeof metadata.documentNumber === 'string') {
    return `#${metadata.documentNumber}`;
  }
  if ('medicalRecordNumber' in metadata && typeof metadata.medicalRecordNumber === 'string') {
    return `MRN: ${metadata.medicalRecordNumber}`;
  }
  return null;
};

type DocumentCardPropsWithIndex = DocumentCardProps & {
  index?: number;
  totalCards?: number;
};

export function DocumentCard({ document, onPress, onDelete, index = 0, totalCards = 1 }: DocumentCardPropsWithIndex) {
  const theme = useTheme();
  const [isOffline, setIsOffline] = useState(false);
  
  const iconName = DOCUMENT_ICONS[document.type] || DOCUMENT_ICONS.other;
  const typeColors = DOCUMENT_TYPE_COLORS[document.type] || DOCUMENT_TYPE_COLORS.other;
  const typeColor = typeColors.primary;
  const gradientColors: [string, string, string] = Array.isArray(typeColors.gradient)
    ? (typeColors.gradient as [string, string, string])
    : [typeColor, typeColor, typeColor];
  
  // Get expiration date (checks both expiresOn and metadata)
  const expirationDate = getDocumentExpirationDate(document);
  const expirationStatus = getExpirationStatus(expirationDate);
  const metadataIdentifier = getMetadataIdentifier(document.metadata);

  // Check offline status
  useEffect(() => {
    if (document.entryType === 'manual') {
      setIsOffline(false);
      return;
    }
    
    const checkOffline = async () => {
      try {
        const offline = await isDocumentOffline(document.id);
        setIsOffline(offline);
      } catch (error) {
        console.error('[DocumentCard] Error checking offline status:', error);
        setIsOffline(false);
      }
    };
    
    checkOffline();
  }, [document.id, document.entryType]);
  
  const formatExpirationDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const formatFileSize = (bytes: number | null | undefined) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatRemaining = () => {
    if (!expirationStatus) return '';
    const days = expirationStatus.daysUntil;

    const pluralize = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`;

    if (expirationStatus.status === 'expired') {
      return `${pluralize(days, 'day')} ago`;
    }

    if (days >= 365) {
      const years = Math.floor(days / 365);
      const months = Math.floor((days % 365) / 30);
      if (months > 0) {
        return `${pluralize(years, 'yr')} ${pluralize(months, 'mo')}`;
      }
      return pluralize(years, 'yr');
    }

    if (days >= 60) {
      const months = Math.floor(days / 30);
      return pluralize(months, 'mo');
    }

    return pluralize(days, 'day');
  };

  // Calculate stack offset for wallet-style overlapping
  const stackOffset = index * 8; // 8px offset per card
  const scale = 1 - (index * 0.02); // Slight scale down for depth
  const zIndex = totalCards - index; // Higher cards have higher z-index
  
  // Determine if card should have light or dark text based on background
  const isDarkBackground = ['passport', 'visa', 'insurance'].includes(document.type);
  const textColor = isDarkBackground ? '#FFFFFF' : theme.colors.text;
  const textSecondaryColor = isDarkBackground ? 'rgba(255, 255, 255, 0.8)' : theme.colors.textSecondary;
  
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.cardContainer,
        {
          marginTop: index > 0 ? -stackOffset : theme.spacing(2),
          marginBottom: index === totalCards - 1 ? theme.spacing(2) : 0,
          transform: [{ scale: pressed ? 0.98 : scale }],
          zIndex,
          opacity: pressed ? 0.9 : 1,
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${document.name}, ${document.type || 'document'}${expirationDate ? `, expires ${formatExpirationDate(expirationDate)}` : ''}`}
      accessibilityHint="Double tap to view document details"
    >
      <View
        style={[
          styles.card,
          {
            borderRadius: theme.radii.xl,
            ...theme.shadows.level2,
          },
        ]}
      >
        {/* Gradient Header */}
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <View style={[styles.iconContainer, { backgroundColor: 'rgba(255, 255, 255, 0.2)' }]}>
                <Ionicons name={iconName} size={24} color="#FFFFFF" />
              </View>
              <View style={styles.headerText}>
                <ThemedText
                  variant="bodyStrong"
                  style={[styles.documentName, { color: textColor }]}
                  numberOfLines={1}
                >
                  {document.name}
                </ThemedText>
                {metadataIdentifier && (
                  <Caption style={[styles.documentNumber, { color: textSecondaryColor }]}>
                    {metadataIdentifier}
                  </Caption>
                )}
              </View>
            </View>
            <View style={styles.headerRight}>
            {document.entryType !== 'manual' && document.storagePath && (
              <TouchableOpacity
                onPress={(e) => {
                  if (e && typeof (e as any).stopPropagation === 'function') {
                    (e as any).stopPropagation();
                  }
                  onPress();
                }}
                activeOpacity={0.7}
                style={styles.deleteButton}
                accessibilityRole="button"
                accessibilityLabel={`View attachment for ${document.name}`}
                accessibilityHint="Opens the attached file"
              >
                <Ionicons name="open-outline" size={18} color={textColor} />
              </TouchableOpacity>
            )}
              {onDelete && (
                <TouchableOpacity
                  onPress={(e) => {
                    // RN-web sometimes provides a synthetic event without stopPropagation
                    // (or it can behave inconsistently). Guard to avoid breaking delete.
                    if (e && typeof (e as any).stopPropagation === 'function') {
                      (e as any).stopPropagation();
                    }
                    console.log('[DocumentCard] Delete pressed for:', document.id);
                    onDelete();
                  }}
                  activeOpacity={0.7}
                  style={styles.deleteButton}
                  accessibilityRole="button"
                  accessibilityLabel={`Delete ${document.name}`}
                  accessibilityHint="Deletes this document permanently"
                >
                  <Ionicons name="trash-outline" size={18} color={textColor} />
                </TouchableOpacity>
              )}
            </View>
          </View>
        </LinearGradient>

        {/* Card Body */}
        <View
          style={[
            styles.body,
            {
              backgroundColor: theme.colors.surface,
            },
          ]}
        >
          {/* Badges Row */}
          <View style={styles.badgesRow}>
            {document.entryType !== 'manual' && document.storagePath && (
              <View style={[styles.badge, { backgroundColor: theme.colors.primary + '14', borderColor: theme.colors.primary + '35' }]}>
                <Ionicons name="attach-outline" size={12} color={theme.colors.primary} />
                <Caption style={[styles.badgeText, { color: theme.colors.primary }]}>
                  Attachment
                </Caption>
              </View>
            )}
            {document.encryptionMethod && document.encryptionMethod !== 'none' && (
              <View style={[styles.badge, { backgroundColor: theme.colors.successContainer }]}>
                <Ionicons name="lock-closed" size={12} color={theme.colors.success} />
                <Caption style={[styles.badgeText, { color: theme.colors.success }]}>
                  Encrypted
                </Caption>
              </View>
            )}
            {document.entryType === 'manual' && (
              <View style={[styles.badge, { backgroundColor: theme.colors.primaryContainer }]}>
                <Caption style={[styles.badgeText, { color: theme.colors.primary }]}>
                  Manual
                </Caption>
              </View>
            )}
            {isOffline && document.entryType !== 'manual' && (
              <View style={[styles.badge, { backgroundColor: theme.colors.successContainer }]}>
                <Ionicons name="cloud-done" size={12} color={theme.colors.success} />
                <Caption style={[styles.badgeText, { color: theme.colors.success }]}>
                  Offline
                </Caption>
              </View>
            )}
          </View>

          {/* Expiration Info */}
          <View style={styles.footer}>
            {expirationDate ? (
              <View style={styles.expirationRow}>
                <Caption color={theme.colors.textSecondary} style={styles.expirationText}>
                  Expires {formatExpirationDate(expirationDate)}
                </Caption>
                {expirationStatus && (
                  <View
                    style={[
                      styles.statusBadge,
                      {
                        backgroundColor: expirationStatus.color + '15',
                        borderColor: expirationStatus.color + '30',
                      },
                    ]}
                  >
                    <Caption
                      style={[
                        styles.statusText,
                        {
                          color: expirationStatus.color,
                        },
                      ]}
                    >
                      {expirationStatus.status === 'expired'
                        ? `⚠️ ${formatRemaining()}`
                        : expirationStatus.status === 'expiring_very_soon'
                        ? `⚠️ ${formatRemaining()}`
                        : expirationStatus.status === 'expiring_soon'
                        ? `⏰ ${formatRemaining()}`
                        : `✓ ${formatRemaining()}`}
                    </Caption>
                  </View>
                )}
              </View>
            ) : (
              <Caption color={theme.colors.textSecondary} style={styles.expirationText}>
                No expiration date
              </Caption>
            )}
            {document.size && (
              <Caption color={theme.colors.textSecondary} style={styles.fileSize}>
                {formatFileSize(document.size)}
              </Caption>
            )}
          </View>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    marginHorizontal: 16,
    minHeight: 44, // WCAG minimum touch target
  },
  card: {
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    minHeight: 80,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  documentName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  documentNumber: {
    fontSize: 13,
    fontWeight: '500',
    letterSpacing: 0.1,
  },
  headerRight: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButton: {
    padding: 8,
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
  },
  badgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  footer: {
    gap: 6,
  },
  expirationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  expirationText: {
    fontSize: 13,
    fontWeight: '500',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  fileSize: {
    fontSize: 12,
    fontWeight: '400',
  },
});

